import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, ShieldAlert } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useSessionState } from '@/hooks/use-session-state';

/** Kontrol: posted entries whose debits and credits do not tie (travel). */
export function UnbalancedEntriesReport() {
  const [startDate, setStartDate] = useSessionState('travel:accounting-reports:checks:startDate', '');
  const [endDate, setEndDate] = useSessionState('travel:accounting-reports:checks:endDate', '');
  const { data, isLoading, error } = trpc.financialReports.getUnbalancedEntries.useQuery({
    startDate: startDate ? new Date(startDate) : undefined,
    endDate: endDate ? new Date(endDate) : undefined,
  });

  return (
    <div className="space-y-4">
      <div className="bg-card flex flex-wrap items-end gap-4 rounded-lg border p-4 print:hidden">
        <div className="space-y-2">
          <Label>Başlangıç / From (boş = tümü)</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-44" />
        </div>
        <div className="space-y-2">
          <Label>Bitiş / To</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-44" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : error ? (
        <div className="border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">{error.message}</div>
      ) : data ? (
        <>
          <div
            className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
              data.count === 0
                ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-100'
                : 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-900/20 dark:text-red-100'
            }`}
          >
            {data.count === 0 ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>
              {data.count === 0
                ? 'Tüm işlenmiş fişler dengeli. / Every posted entry balances.'
                : `${data.count} fişin borç ve alacağı eşit değil (net fark ${formatCurrency(data.netDifference)}). / ${data.count} posted entries do not balance.`}
            </span>
          </div>
          {data.count > 0 && (
            <div className="bg-card overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 font-medium">Fiş</th>
                    <th className="px-4 py-2 font-medium">Tarih</th>
                    <th className="px-4 py-2 font-medium">Açıklama</th>
                    <th className="px-4 py-2 text-right font-medium">Borç</th>
                    <th className="px-4 py-2 text-right font-medium">Alacak</th>
                    <th className="px-4 py-2 text-right font-medium">Fark</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e) => (
                    <tr key={e.id} className="border-b last:border-b-0">
                      <td className="px-4 py-2">
                        <Link to={`/travel/accounting/journal-entries/${e.id}`} className="font-mono text-xs hover:underline">
                          {e.entryNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">{formatDate(e.date)}</td>
                      <td className="px-4 py-2">{e.description}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatCurrency(e.totalDebit)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatCurrency(e.totalCredit)}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold text-red-700 dark:text-red-300">{formatCurrency(e.difference)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
